import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";

import { Button, Col, Result, Row, Skeleton } from "antd";

import { CmtPage } from "@app/components/page/CmtPage.tsx";
import { CmtUserContractPeriods } from "@app/components/users/CmtUserContractPeriods.tsx";
import { CmtUserHero } from "@app/components/users/CmtUserHero.tsx";
import { CmtUserInformation } from "@app/components/users/CmtUserInformation.tsx";
import { CmtUserMissions } from "@app/components/users/CmtUserMissions.tsx";
import { CmtUserRoles } from "@app/components/users/CmtUserRoles.tsx";
import { useUser } from "@app/services/hook/useUser.tsx";
import { showNotification } from "@app/services/notification/showNotification.tsx";

export const UserPage = () => {
  const { userId } = useParams();
  const { user, error, isLoading } = useUser(userId!);
  const intl = useIntl();

  useEffect(() => {
    if (error) {
      showNotification({
        type: "error",
        title: intl.formatMessage({
          id: "error.title",
          defaultMessage: "Error",
        }),
        description: intl.formatMessage(
          {
            id: "error.user.description",
            defaultMessage: "Failed to access user ({userId})",
          },
          { userId },
        ),
      });
    }
  }, [error, intl, userId]);

  if (isLoading) {
    return (
      <CmtPage goBack>
        <Skeleton avatar={{ size: 128 }} active paragraph={{ rows: 6 }} />
      </CmtPage>
    );
  }

  if (!user) {
    return (
      <Result
        status="404"
        title={
          <FormattedMessage
            id="error.user.not_found"
            defaultMessage="Employee not found"
          />
        }
        extra={
          <Link to="/">
            <Button type="primary">
              <FormattedMessage
                id="error.back_to_home"
                defaultMessage="Back to home"
              />
            </Button>
          </Link>
        }
      />
    );
  }

  return (
    <CmtPage goBack>
      <CmtUserHero user={user} />
      <Row gutter={[24, 24]}>
        <Col xs={24} xl={12}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <CmtUserInformation user={user} />
            </Col>
            <Col span={24}>
              <CmtUserRoles roles={user.roles} />
            </Col>
          </Row>
        </Col>
        <Col xs={24} xl={12}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <CmtUserMissions email={user.email} />
            </Col>
            <Col span={24}>
              <CmtUserContractPeriods user={user} />
            </Col>
          </Row>
        </Col>
      </Row>
    </CmtPage>
  );
};
